import type { graphTypeEnum } from '@/features/analysis/_models/project';
import type { SorterResult } from 'antd/es/table/interface';
import type { z } from 'zod';
import type { KsTableRow } from './get-ks-table-datasource';
import { KS_ORDER_SEPARATOR } from './resolve-ks-ordering';

type GraphType = z.infer<typeof graphTypeEnum>;

/**
 * テーブルのソート変更を URL クエリへ反映するための差分を構築する。
 * 行集合の意味が変わるため、page は必ず 1 に戻す。
 *
 * 表示年度（`chartYear`）には一切触れない。列ヘッダのソートは「テーブルの並び順の操作」であり、
 * 表示年度とは独立した操作として扱う（例: 2026年度の円グラフを見たまま 2025 列をソートしても
 * グラフの表示年度は 2026 のまま）。ソート解除時に書き換えると RS-4491 の修正
 * （ソート解除で表示年度を戻さない）も崩れる。表示年度の変更は年度タブ（`handleSelectYear`）のみが担う。
 */
export const buildSortUpdate = (
  sorter: Pick<SorterResult<KsTableRow>, 'order' | 'columnKey'>,
): { page: 1; order: string | null } => {
  if (sorter.order && sorter.columnKey !== undefined) {
    return {
      page: 1,
      order: `${sorter.order}${KS_ORDER_SEPARATOR}${sorter.columnKey}`,
    };
  }
  return { page: 1, order: null };
};

/**
 * 検索フォーム送信時に必ず必要な差分（page を 1 に戻す）。
 * 結果件数が減ったときに範囲外のページが残り続けないようにするため。
 */
export const buildSubmitPageReset = (): { page: 1 } => ({ page: 1 });

/**
 * 円グラフ・ツリーマップの年度タブで年度を選択したときの URL クエリ差分を構築する。
 *
 * 表示年度は `chartYear` が担う。手動ソート(`order`)の扱いは軸で分かれる:
 * - 事業所管課室(organization)軸: `order` には触れない（ソート状態継続, RS-4487）。
 *   書き込むと GIMA コード順が金額順に変わる仕様違反 (RS-4492)、クリアすると手動ソートが
 *   解除されてしまうため、`order` キー自体を差分に含めない。テーブルの並びが変わらないので
 *   page もリセットしない。
 * - それ以外の軸: `order` をクリアする。`resolveKsSortKeys` / `getKsColumnSortOrder` が
 *   `chartYear` 基準の降順にフォールバックし、「年度タブ選択＝選択年度の降順・▽付き」の
 *   仕様を満たす (RS-4487)。
 */
export const buildYearSelectUpdate = (
  year: number,
  isOrganizationAxis: boolean,
): { chartYear: number; order?: null; page?: 1 } => ({
  chartYear: year,
  ...(isOrganizationAxis ? {} : { order: null, page: 1 }),
});

/**
 * グラフ種別切替時の URL クエリ差分を構築する。
 *
 * 円/ツリーの表示年度(`chartYear`)は軸に関係なく常にリセットし、切替先で既定選択年度
 * （前年度 n-1 優先）から表示する (RS-4491)。
 * 手動ソート(`order`)は、事業所管課室(organization)軸はグラフ種別ごとの既定ソートを
 * 持たない（常に GIMA コード順 or 手動ソート）ため維持し、それ以外の軸はそのグラフ既定の
 * 初期ソートへ戻すためリセットする (RS-4487)。
 */
export const buildGraphTypeChangeUpdate = (
  graphType: GraphType,
  isOrganizationAxis: boolean,
): {
  graphType: GraphType;
  chartYear: null;
  order?: null;
  page?: 1;
} => ({
  graphType,
  chartYear: null,
  ...(isOrganizationAxis ? {} : { order: null, page: 1 }),
});
